import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { historyPush } from 'actions/core/navigation'
import { rootApi } from 'root-api'

import {
  addManagedEventTypeMemberships,
  destroyManagedEventTypeMembership,
  getManagedEventType,
  getManagedEventTypeMemberships,
  getOrganizationMembershipsWithSlugConflicts
} from '../api'
import { unassignPopupMembershipIdSelector } from './selectors'

export const fetchManagedEventType = createAsyncThunk(
  'managedEventType/fetch',
  async ({ id }, { rejectWithValue, dispatch }) => {
    const [success, response] = await getManagedEventType(id)

    if (!success) {
      const url = '/app/admin/managed_event_types'

      dispatch(historyPush(url))
      dispatch(showHttpError(response))
      return rejectWithValue(null)
    }

    return response
  }
)

export const fetchGroupMemberships = createAsyncThunk('managedEventType/fetchMemberships', async (managedEventTypeId, { dispatch }) => {
  const [success, response] = await getManagedEventTypeMemberships(managedEventTypeId, { scope: 'groups' })

  if (!success) {
    dispatch(showHttpError(response))
    return false
  }

  return response
})

const openSlugConflictsPopup = createAction('managedEventType/openSlugConflictsPopup')
export const closeSlugConflictsPopup = createAction('managedEventType/closeSlugConflictsPopup')
const setAssignPopupMembershipIds = createAction('managedEventType/setMembershipIds')

export const assignManagedEventTypeMemberships = createAsyncThunk(
  'managedEventType/assign',
  async ({ managedEventTypeId, membershipIds, invitationIds, groupIds, checkSlugConflicts },
    { dispatch, rejectWithValue }) => {
    if (checkSlugConflicts && (membershipIds.length > 0 || groupIds.length > 0)) {
      dispatch(setAssignPopupMembershipIds({ membershipIds, invitationIds, groupIds }))

      const [membershipsWithSlugConflictSuccess, membershipsWithSlugConflictResponse] = await getOrganizationMembershipsWithSlugConflicts(
        [managedEventTypeId], membershipIds, groupIds
      )

      if (!membershipsWithSlugConflictSuccess) {
        dispatch(showHttpError(membershipsWithSlugConflictResponse))
        return rejectWithValue({})
      }

      if (membershipsWithSlugConflictResponse.conflicts.length > 0) {
        dispatch(openSlugConflictsPopup(membershipsWithSlugConflictResponse))
        return rejectWithValue({})
      }
    }

    const [success, response] = await addManagedEventTypeMemberships(
      managedEventTypeId,
      invitationIds,
      membershipIds,
      groupIds
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue({})
    }

    const flashMessage = groupIds.length > 0
      ? I18n.t(
        'publisher.organization.managed_event_types.assign.add_groups.success_message',
        { count: groupIds.length }
      )
      : I18n.t(
        'publisher.organization.managed_event_types.assign.add_members.success_message',
        { count: membershipIds.length + invitationIds.length }
      )

    dispatch(showSuccess(flashMessage))

    // Invalidate the RTK Query cache to refresh the managed event types list
    dispatch(rootApi.util.invalidateTags(['MANAGED_EVENT_TYPE_LIST']))
    dispatch(fetchManagedEventType({ id: managedEventTypeId }))
    dispatch(fetchGroupMemberships(managedEventTypeId))

    return true
  }
)

export const deleteManagedEventTypeMemberships = createAsyncThunk(
  'managedEventType/deleteMembership',
  async ({ managedEventTypeId }, { getState, dispatch }) => {
    const state = getState()
    const membershipId = unassignPopupMembershipIdSelector(state)
    const [success, response] = await destroyManagedEventTypeMembership(managedEventTypeId, membershipId)

    if (!success) {
      dispatch(showHttpError(response))
      return
    }

    // Invalidate the RTK Query cache to refresh the managed event types list
    dispatch(rootApi.util.invalidateTags(['MANAGED_EVENT_TYPE_LIST']))
    dispatch(fetchManagedEventType({ id: managedEventTypeId }))
    dispatch(fetchGroupMemberships(managedEventTypeId))
  }
)

const initialDetailsPopup = {
  isOpen: false,
  scope: 'active',
  groupId: null,
  userGroup: {},
  entities: [],
  page: 1,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: null,
    pageSize: null,
    rangeStart: null,
    rangeEnd: null
  },
  isLoading: true,
}

const slice = createSlice({
  name: 'managedEventTypes/assign',
  initialState: {
    status: 'initial',
    eventType: {},
    memberships: {
      groups: {
        status: 'initial',
        entities: []
      }
    },
    assignPopup: {
      groupIds: [],
      invitationIds: [],
      isOpen: false,
      membershipIds: [],
      membershipsWithSlugConflict: [],
      scope: null,
      submitting: false,
      userGroupId: null
    },
    unassignPopup: {
      isOpen: false,
      membershipId: null,
      owner: null,
      submitting: false,
    },
    detailsPopup: initialDetailsPopup,
    isAssignmentByOwningGroupPending: false
  },
  reducers: {
    openAssignPopup(state, { payload }) {
      state.assignPopup.isOpen = true
      state.assignPopup.scope = payload.scope
      state.assignPopup.userGroupId = payload.userGroupId
    },
    closeAssignPopup(state) {
      state.assignPopup.groupIds = []
      state.assignPopup.invitationIds = []
      state.assignPopup.isOpen = false
      state.assignPopup.membershipIds = []
      state.assignPopup.membershipsWithSlugConflict = []
      state.assignPopup.scope = null
      state.assignPopup.userGroupId = null
    },
    openUnassignPopup(state, action) {
      const { payload: { membershipId, owner } } = action
      state.unassignPopup.membershipId = membershipId
      state.unassignPopup.isOpen = true
      state.unassignPopup.owner = owner
    },
    closeUnassignPopup(state) {
      state.unassignPopup.membershipId = null
      state.unassignPopup.isOpen = false
      state.unassignPopup.submitting = false
      state.unassignPopup.owner = null
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchManagedEventType.pending, (state) => {
      state.status = 'loading'
    })
    builder.addCase(fetchManagedEventType.fulfilled, (state, action) => {
      state.status = 'loaded'
      state.eventType = action.payload
    })
    builder.addCase(fetchManagedEventType.rejected, (state) => {
      state.status = 'failed'
    })
    builder.addCase(assignManagedEventTypeMemberships.pending, (state) => {
      state.assignPopup.submitting = true
    })
    builder.addCase(assignManagedEventTypeMemberships.fulfilled, (state) => {
      state.assignPopup.submitting = false
      state.assignPopup.isOpen = false
      state.assignPopup.membershipsWithSlugConflict = []
    })
    builder.addCase(assignManagedEventTypeMemberships.rejected, (state) => {
      state.assignPopup.submitting = false
    })
    builder.addCase(deleteManagedEventTypeMemberships.pending, (state) => {
      state.unassignPopup.submitting = true
    })
    builder.addCase(deleteManagedEventTypeMemberships.fulfilled, (state) => {
      state.unassignPopup.submitting = false
      state.unassignPopup.membershipId = null
      state.unassignPopup.isOpen = false
    })
    builder.addCase(deleteManagedEventTypeMemberships.rejected, (state) => {
      state.unassignPopup.submitting = false
      state.unassignPopup.membershipId = null
      state.unassignPopup.isOpen = false
    })
    builder.addCase(openSlugConflictsPopup, (state, action) => {
      state.assignPopup.membershipsWithSlugConflict = action.payload.memberships
    })
    builder.addCase(closeSlugConflictsPopup, (state) => {
      state.assignPopup.membershipsWithSlugConflict = []
    })
    builder.addCase(setAssignPopupMembershipIds, (state, action) => {
      state.assignPopup.membershipIds = action.payload.membershipIds
      state.assignPopup.invitationIds = action.payload.invitationIds
      state.assignPopup.groupIds = action.payload.groupIds
    })
    builder.addCase(fetchGroupMemberships.pending, (state) => {
      state.memberships.groups.status = 'loading'
    })
    builder.addCase(fetchGroupMemberships.rejected, (state) => {
      state.memberships.groups.status = 'failed'
      state.memberships.groups.entities = []
    })
    builder.addCase(fetchGroupMemberships.fulfilled, (state, action) => {
      state.memberships.groups.status = 'loaded'
      state.memberships.groups.entities = action.payload
    })
  }
})

export const {
  reducer,
  actions: {
    openAssignPopup,
    closeAssignPopup,
    openUnassignPopup,
    closeUnassignPopup,
  }
} = slice
